import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import SearchAutosuggest from "./SearchAutosuggest";

/**
 * Header search trigger: shows a Search icon button. On click, opens a centered
 * dropdown panel anchored under the header that hosts the autosuggest UI.
 * Closes on outside click, Escape, or after a suggestion is chosen.
 */
const HeaderSearchTrigger = () => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-md hover:bg-[var(--bg-secondary)] transition-colors"
        aria-label="Search"
        aria-expanded={open}
        data-testid="header-search-btn"
      >
        <Search size={20} color="var(--text-heading)" />
      </button>

      {open && (
        <>
          <div className="vd-search-backdrop" aria-hidden="true" />
          <div ref={panelRef} className="vd-search-overlay" role="dialog" aria-label="Search" data-testid="header-search-overlay">
            <div className="vd-search-overlay-inner">
              <SearchAutosuggest onClose={() => setOpen(false)} />
            </div>
          </div>
        </>
      )}

      <style>{`
        .vd-search-backdrop {
          position: fixed; inset: 0; background: rgba(0,0,0,0.35);
          z-index: 60; backdrop-filter: blur(2px);
          animation: vdFade 160ms ease;
        }
        .vd-search-overlay {
          position: fixed; left: 50%; top: 96px; transform: translateX(-50%);
          width: min(640px, calc(100vw - 24px));
          z-index: 70;
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 14px;
          padding: 12px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          animation: vdSlideDown 200ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .vd-search-overlay-inner { width: 100%; }
        @keyframes vdSlideDown { from { opacity: 0; transform: translate(-50%, -10px); } to { opacity: 1; transform: translate(-50%, 0); } }
        @keyframes vdFade { from { opacity: 0; } to { opacity: 1; } }
        @media (max-width: 640px) {
          .vd-search-overlay { top: 80px; }
        }
      `}</style>
    </>
  );
};

export default HeaderSearchTrigger;
